export const CLIENT_RELATIONSHIP_TYPE_LABELS = {
  familiar: "Familiar",
  conjuge: "Cônjuge",
  responsavel: "Responsável",
  dependente: "Dependente",
  socio: "Sócio",
  contato_comercial: "Contato comercial",
  indicador: "Indicador",
  funcionario: "Funcionário",
  outro: "Outro",
};

/** Tipos típicos de vínculo pessoa ↔ empresa no atendimento B2B. */
const B2B_RELATIONSHIP_TYPE_SCORE = {
  funcionario: 100,
  socio: 95,
  contato_comercial: 90,
  responsavel: 85,
  indicador: 80,
  dependente: 55,
  outro: 40,
  familiar: 10,
  conjuge: 10,
};

export function getClientRelationshipTypeLabel(value) {
  return CLIENT_RELATIONSHIP_TYPE_LABELS[value] || value || "Outro";
}

export function isClientTipoPJ(tipo) {
  const n = Number(tipo);
  if (Number.isFinite(n)) return n === 1;
  const s = String(tipo ?? "")
    .trim()
    .toLowerCase();
  return s.includes("jur") || s === "pj" || s === "company";
}

export function getClientRelationshipPeer(rel, clientId) {
  if (!rel || clientId == null) return null;
  const baseId = Number(clientId);
  const isFrom = Number(rel.client_id_from) === baseId;
  const peerId = isFrom ? rel.client_id_to : rel.client_id_from;
  if (peerId == null || Number(peerId) === baseId) return null;
  return {
    clientId: Number(peerId),
    nome: (isFrom ? rel.to_name : rel.from_name) || "",
    tipo: isFrom ? rel.to_tipo : rel.from_tipo,
    isFrom,
  };
}

export function getClientRelationshipDirectionLabel(rel, clientId) {
  if (!rel) return "";
  if (rel.is_bidirectional) return "Bidirecional";
  const peer = getClientRelationshipPeer(rel, clientId);
  if (!peer) return "Unidirecional";
  return peer.isFrom ? "Origem" : "Destino";
}

function relationshipTypeScore(type) {
  return B2B_RELATIONSHIP_TYPE_SCORE[type] || 0;
}

/**
 * Escolhe o vínculo mais útil para o badge do chat.
 * Em B2B (PF ↔ PJ), prioriza a empresa relacionada (funcionário, sócio, etc.).
 */
export function pickPrimaryClientRelationship(relationships, clientId) {
  const list = Array.isArray(relationships) ? relationships : [];
  let best = null;
  let bestScore = -1;

  for (const rel of list) {
    const peer = getClientRelationshipPeer(rel, clientId);
    if (!peer) continue;
    const peerIsPj = isClientTipoPJ(peer.tipo);
    const typeScore = relationshipTypeScore(rel.relationship_type);
    // Empresa relacionada pesa mais — é o que o atendente precisa ver na lista/header.
    const score = (peerIsPj ? 1000 : 0) + typeScore;
    if (score > bestScore) {
      bestScore = score;
      best = { rel, peer, peerIsPj, typeScore };
    }
  }
  return best;
}

/**
 * Monta label/tooltip do indicador sutil de relacionamento.
 * @param {object} opts
 * @param {Array} [opts.relationships]
 * @param {number|string|null} [opts.clientId]
 * @param {number} [opts.count]
 * @param {object|null} [opts.hint] - resumo vindo da lista (`relationshipHint`)
 * @param {boolean} [opts.compact]
 */
export function resolveClientRelationshipBadgeSummary({
  relationships = null,
  clientId = null,
  count = 0,
  hint = null,
  compact = false,
} = {}) {
  const list = Array.isArray(relationships) ? relationships : null;

  if (list?.length) {
    const lines = list
      .map((rel) => {
        const peer = getClientRelationshipPeer(rel, clientId);
        if (!peer) return null;
        const type = getClientRelationshipTypeLabel(rel.relationship_type);
        const direction = getClientRelationshipDirectionLabel(rel, clientId);
        const name = peer.nome || `Cliente #${peer.clientId}`;
        return `${type} · ${direction} · ${name}`;
      })
      .filter(Boolean);
    if (!lines.length) return null;

    const primary = pickPrimaryClientRelationship(list, clientId);
    const typeLabel = getClientRelationshipTypeLabel(primary?.rel?.relationship_type);
    const companyOrPeer = primary?.peer?.nome || "";
    const preferCompany = Boolean(primary?.peerIsPj && companyOrPeer);

    let label;
    if (preferCompany) {
      label =
        lines.length === 1
          ? companyOrPeer
          : compact
          ? companyOrPeer
          : `${companyOrPeer} +${lines.length - 1}`;
    } else if (lines.length === 1) {
      label = typeLabel;
    } else {
      label = compact ? String(lines.length) : `${typeLabel} +${lines.length - 1}`;
    }

    return {
      count: lines.length,
      tooltip: lines.join("\n"),
      label,
      peerName: companyOrPeer,
      prefersCompany: preferCompany,
    };
  }

  if (hint && (hint.label || hint.peerName)) {
    const n = Number(hint.count ?? count) || 0;
    const peerName = String(hint.peerName || "").trim();
    const typeLabel = getClientRelationshipTypeLabel(hint.relationshipType || hint.type);
    const prefersCompany = Boolean(hint.peerIsPj && peerName);
    const label =
      String(hint.label || "").trim() ||
      (prefersCompany ? peerName : compact && n > 0 ? String(n) : typeLabel || "Relacionamento");
    const tooltip =
      String(hint.tooltip || "").trim() ||
      (peerName
        ? `${typeLabel || "Relacionamento"} · ${peerName}`
        : n === 1
        ? "1 relacionamento"
        : `${n} relacionamentos`);
    if (!label && n <= 0) return null;
    return {
      count: n || 1,
      tooltip,
      label: label || (n === 1 ? "Relacionamento" : `${n} relacionamentos`),
      peerName,
      prefersCompany,
    };
  }

  const n = Number(count) || 0;
  if (n <= 0) return null;
  return {
    count: n,
    tooltip: n === 1 ? "1 relacionamento" : `${n} relacionamentos`,
    label: compact ? String(n) : n === 1 ? "Relacionamento" : `${n} relacionamentos`,
    peerName: "",
    prefersCompany: false,
  };
}

/** Lê o resumo de relacionamentos vindo da lista de clientes/chat. */
export function getClientListRelationshipProps(client) {
  if (!client || typeof client !== "object") {
    return { count: 0, hint: null };
  }
  const count = Number(client.relationshipsCount ?? client.relationships_count) || 0;
  const hint = client.relationshipHint ?? client.relationship_hint ?? null;
  return { count, hint };
}
